import { makeAutoObservable } from 'mobx';
import analyticsService from '../service/analytics-service/analytics.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

class AnalyticsStore {
  initializationPromise: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async sendCurrentPageEvent(pathName: string, pageTitle: string) {
    try {
      const communitySet = await this.setCommunityProperties();

      if (!communitySet) {
        console.warn('Community property was not set, event skipped.');
        return;
      }

      await analyticsService.setCurrentPage(pathName, pageTitle);
    } catch (error) {
      console.error('Failed to send page event', error);
    }
  }

  private async setCommunityProperties() {
    await this.initialize();

    try {
      const communityName = await this.getCommunityName();

      if (!communityName) {
        console.warn('Community name not found, cannot set property.');
        return false;
      }

      const appVersion = await this.getAppVersion();

      await analyticsService.setUserCommunity(communityName, appVersion);

      return true;
    } catch (error) {
      console.error('Failed to set community property', error);
      return false;
    }
  }

  private async getAppVersion() {
    if (Capacitor.getPlatform() === 'web') {
      return process.env.REACT_APP_RELEASE_NAME || 'Web';
    }

    const appInfo = await App.getInfo();
    return appInfo.version;
  }

  private async initialize() {
    try {
      this.initializationPromise ??= analyticsService.initializeAnalytics();
      await this.initializationPromise;
    } catch (error) {
      console.error('Analytics initialization failed', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  private async getCommunityName() {
    try {
      let communityName = configurationStore.configuration?.default.communityName;

      if (!communityName) {
        await configurationStore.requestConfiguration();
        communityName = configurationStore.configuration?.default.communityName;
      }

      return communityName;
    } catch (error) {
      console.error('Failed to get community name', error);
      return undefined;
    }
  }
}

const analyticsStore = new AnalyticsStore();

export default analyticsStore;
